<template>
  <div>
    <b-form class="mt-2" @submit.prevent="onSubmit">
      <b-row class="mt-5">
        <b-col cols="3">
          <b-form-row>
            <b-col
              offset-md="2"
              offset-lg="3">
              <avatar-form
                :avatar="avatar"
                :isReadonly="isReadonly"
                @onUpdateAvatar="updateAvatar"></avatar-form>
            </b-col>
          </b-form-row>
        </b-col>

        <b-col>
          <b-form-group
            label-for="salutation"
            :label="$t('message.personalInformationForm.label.salutation')"
            :state="$v.salutation.$error ? false : null"
            :invalid-feedback="$t('error.personalInformationForm.salutation')">
            <b-form-select
              v-if="!isReadonly"
              id="salutation"
              class="rounded-0"
              v-model="salutation"
              :options="salutations"
              :state="$v.salutation.$error ? false : null"
              @changed="$v.salutation.$touch()">
            </b-form-select>
            <b-form-input
              v-else
              id="salutation"
              class="rounded-0"
              :disabled="isReadonly"
              :value="$t('enums.salutations.' + personalInfo.salutation)"
              :class="{'text-muted': isReadonly}">
            </b-form-input>
          </b-form-group>

          <b-form-group
            label-for="first-name"
            :label="$t('message.personalInformationForm.label.firstName')"
            :state="$v.firstName.$error ? false : null"
            :invalid-feedback="$t('error.personalInformationForm.firstName')">
            <b-form-input
              id="first-name"
              class="rounded-0"
              v-model="firstName"
              :disabled="isReadonly"
              :state="$v.firstName.$error ? false : null"
              @changed="$v.firstName.$touch()">
            </b-form-input>
          </b-form-group>

          <b-form-group
            label-for="date-of-birth"
            :label="$t('message.personalInformationForm.label.dateOfBirth')"
            :state="$v.dateOfBirth.$error ? false : null"
            :invalid-feedback="$t('error.personalInformationForm.dateOfBirth')">
            <b-form-input
              id="date-of-birth"
              class="rounded-0"
              v-model="dateOfBirth"
              placeholder="TT.MM.JJJJ"
              :class="{'text-muted': isReadonly}"
              :disabled="isReadonly"
              :state="$v.dateOfBirth.$error ? false : null"
              @change="$v.dateOfBirth.$touch()">
            </b-form-input>
          </b-form-group>
        </b-col>

        <b-col>
          <b-form-group
            :label="$t('message.personalInformationForm.label.title')"
            label-for="title">
            <b-form-input
              id="title"
              class="rounded-0"
              v-model="title"
              :class="{'text-muted': isReadonly}"
              :disabled="isReadonly">
            </b-form-input>
          </b-form-group>

          <b-form-group
            label-for="last-name"
            :label="$t('message.personalInformationForm.label.lastName')"
            :state="$v.lastName.$error ? false : null"
            :invalid-feedback="$t('error.personalInformationForm.lastName')">
            <b-form-input
              id="last-name"
              class="rounded-0"
              v-model="lastName"
              :class="{'text-muted': isReadonly}"
              :disabled="isReadonly"
              :state="$v.lastName.$error ? false : null"
              @change="$v.lastName.$touch()">
            </b-form-input>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mt-4">
        <b-col class="text-right">
          <b-btn v-if="isReadonly"
                 variant="primary"
                 @click="setReadonly(!isReadonly)">
            <span class="ti-pencil mr-2"></span>
            {{ $t('message.personalInformationForm.btn.edit') }}
          </b-btn>

          <div v-else>
            <b-btn
              variant="danger"
              @click="cancel">
              <span class="ti-close mr-2"></span>
              {{ $t('message.personalInformationForm.btn.cancel') }}
            </b-btn>
            <b-btn v-if="this.$store.getters.currentUser.avatar"
                   variant="danger"
                   v-b-modal.deleteAvatarModal>
              <span class="ti-trash mr-2"></span>
              {{ $t('message.avatar.btn.delete') }}
            </b-btn>
            <b-btn
              variant="primary"
              @click="submit">
              <span class="ti-save mr-2"></span>
              {{ $t('message.personalInformationForm.btn.save') }}
            </b-btn>
          </div>
        </b-col>
      </b-row>
    </b-form>

    <b-modal id="deleteAvatarModal"
             cancel-variant="primary"
             ok-variant="danger"
             :title="$t('message.avatar.questionTitle')"
             :ok-title="$t('message.avatar.btn.yes')"
             :cancel-title="$t('message.avatar.btn.no')"
             @ok="deleteAvatar">
      <p class="mt-4">{{ $t('message.avatar.question') }}</p>
    </b-modal>
  </div>
</template>

<script>
  import {required} from 'vuelidate/lib/validators'
  import utils from '@/utils'
  import AvatarForm from '@/components/account/AvatarForm'

  export default {
    components: {
      AvatarForm
    },
    props: {
      onSubmit: {
        type: Function,
        required: true
      },
      onSubmitAvatar: {
        type: Function,
        required: true
      },
      onDeleteAvatar: {
        type: Function,
        required: true
      },
      personalInfo: {
        type: Object,
        required: true
      }
    },
    data () {
      return {
        avatar: this.$store.getters.currentUser.avatar ? this.$store.getters.currentUser.avatar : null,
        avatarFile: null,
        dateOfBirth: this.personalInfo.dateOfBirth ? this.$moment(this.personalInfo.dateOfBirth).format('DD.MM.YYYY') : null,
        firstName: this.personalInfo.firstName,
        isReadonly: true,
        lastName: this.personalInfo.lastName,
        maxFileSize: 6 * 1024 * 1024,
        maxHeight: 600,
        maxWidth: 600,
        salutation: this.personalInfo.salutation,
        salutations: [
          {text: this.$t('enums.salutations.MR'), value: 'MR'},
          {text: this.$t('enums.salutations.MRS'), value: 'MRS'}
        ],
        title: this.personalInfo.title
      }
    },
    methods: {
      cancel () {
        this.avatar = this.$store.getters.currentUser.avatar ? this.$store.getters.currentUser.avatar : null
        this.dateOfBirth = this.personalInfo.dateOfBirth ? this.$moment(this.personalInfo.dateOfBirth).format('DD.MM.YYYY') : null
        this.firstName = this.personalInfo.firstName
        this.lastName = this.personalInfo.lastName
        this.salutation = this.personalInfo.salutation
        this.title = this.personalInfo.title
        this.setReadonly(true)
      },
      deleteAvatar () {
        this.onDeleteAvatar().then(() => {
          this.avatar = this.$store.getters.avatar ? this.$store.getters.avatar : null
        })
      },
      updateAvatar (avatarFile, newAvatar) {
        this.avatarFile = avatarFile
        this.avatar = newAvatar
      },
      setReadonly (isReadonly) {
        this.isReadonly = isReadonly
      },
      submit () {
        if (this.avatarFile !== null) {
          this.onSubmitAvatar(this.avatarFile).then(() => {
            this.avatarFile = null
          })
        }

        this.$v.$touch()
        if (!this.$v.$invalid) {
          this.personalInfo.dateOfBirth = this.$moment(this.dateOfBirth, 'DD.MM.YYYY').format('YYYY-MM-DD')
          this.personalInfo.firstName = this.firstName
          this.personalInfo.lastName = this.lastName
          this.personalInfo.salutation = this.salutation
          this.personalInfo.title = this.title
          this.onSubmit(this.personalInfo).then(() => {
            this.setReadonly(!this.isReadonly)
          })
        }
      }
    },
    validations: {
      dateOfBirth: {
        required,
        isValidDate: utils.validators.isValidDate
      },
      firstName: {
        required
      },
      lastName: {
        required
      },
      salutation: {
        required
      }
    },
    notifications: {
      showSuccessMsg: {
        type: 'success'
      }
    }
  }

</script>



// WEBPACK FOOTER //
// src/components/account/PersonalInformationForm.vue